import apiClient from '../utils/apiClient';
import { BillingGuideline, CodingRule, CodingRuleCPT, CodingRuleICD, SOP, SOPDocument, SOPFilters } from '../types/sop';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

type RawBillingGuideline =
  | string
  | {
      id?: string;

      // ✅ NEW backend schema
      category?: string;
      rules?: {
        id?: string;
        description?: string;
        source?: string;
      }[];

      // 🔁 Legacy / AI schema
      title?: string;
      description?: string | { title?: string; description?: string };
    };

export type SOPQuestionResponse = {
  question: string;
  answer: string;
};
// Helper to map backend snake_case to frontend camelCase
const mapExampleToSOP = (data: any): SOP => ({
  id: data.id,
  title: data.title,
  category: data.category,

  providerType: data.provider_type,
  clientId: data.client_id,

  providerInfo: data.provider_info || {},

  workflowProcess: {
    description: data.workflow_process?.description || data.workflow_process?.superbill_source || '',
    eligibilityPortals: data.workflow_process?.eligibilityPortals || data.workflow_process?.eligibility_verification_portals || [],
    postingCharges: data.workflow_process?.postingCharges || data.workflow_process?.posting_charges_rules || []
  },

  billingGuidelines: normalizeBillingGuidelines(data.billing_guidelines),
  payerGuidelines: data.payer_guidelines || [],
  codingRulesCPT: data.coding_rules_cpt || [],
  codingRulesICD: data.coding_rules_icd || [],
  providers: data.providers || [],


  statusId: data.status_id,
  status: data.status,
  createdAt: data.created_at,
  updatedAt: data.updated_at,
  created_by: data.created_by,
  created_by_name: data.created_by_name,
  organisation_id: data.organisation_id,
  organisation_name: data.organisation_name,
  client_name: data.client_name,
  client_npi: data.client_npi,
  documents: data.documents || []
});
type RawSOP = Omit<
  SOP,
  'billingGuidelines' | 'codingRulesCPT' | 'codingRulesICD'
> & {
  billingGuidelines?: RawBillingGuideline[];
  codingRulesCPT?: CodingRuleCPT[];
  codingRulesICD?: CodingRuleICD[];
};


export const normalizeSOP = (raw: RawSOP): SOP => {
  if (!raw) return {} as SOP;
  return {
    ...raw,

    // ---- CATEGORY ----
    category:
      typeof raw.category === 'string'
        ? raw.category
        : raw.category?.title ?? '',

    // ---- BILLING GUIDELINES ----
billingGuidelines: (raw.billingGuidelines ?? []).map((g, i): BillingGuideline => {

  // ✅ HARD TYPE NARROWING (THIS IS THE KEY)
  if (typeof g === 'object' && g !== null && 'category' in g && Array.isArray(g.rules)) {
    return {
      id: g.id ?? `bg_${i}`,
      category: g.category ?? `Guideline ${i + 1}`,
      rules: g.rules.map((r, j) => ({
        id: r.id ?? `rule_${i}_${j}`,
        description: r.description ?? '',
        source: r.source,
      })),
    };
  }

  // 🔁 Legacy / AI / string formats
  let description = '';

  if (typeof g === 'string') {
    description = g;
  } else if (typeof g === 'object' && g !== null) {
    if (typeof g.description === 'string') {
      description = g.description;
    } else if (typeof g.description === 'object' && g.description !== null) {
      description = g.description.description ?? '';
    }
  }

  return {
    id: typeof g === 'object' && g !== null ? g.id ?? `bg_${i}` : `bg_${i}`,
    category:
      typeof g === 'object' && g !== null
        ? g.title ?? `Guideline ${i + 1}`
        : `Guideline ${i + 1}`,
    rules: description
      ? [{ id: `rule_${i}_0`, description }]
      : [],
  };
}),



    // ---- CODING RULES (defensive) ----
codingRulesCPT: (raw.codingRulesCPT ?? []).map((r, i) => ({
  ...r,
  cptCode: r.cptCode ?? "",
  description: r.description ?? "",
})),

codingRulesICD: (raw.codingRulesICD ?? []).map((r, i) => ({
  ...r,
  icdCode: r.icdCode ?? "",
  description: r.description ?? "",
})),
};
};

const normalizeBillingGuidelines = (input: any[]): BillingGuideline[] => {
  if (!input || !Array.isArray(input)) return [];
  return input.map((g, i) => ({
    id: g.id || `bg_${i}`,
    category: g.category || `Guideline ${i + 1}`,
    rules: Array.isArray(g.rules)
      ? g.rules.map((r: any, j: number) => ({
          id: r.id || `rule_${i}_${j}`,
          description: r.description || "",
          source: r.source
        }))
      : []
  }));
};

const sopService = {
  getSOPs: async (filters: SOPFilters): Promise<{ sops: SOP[]; total: number }> => {
    const { skip = 0, limit = 100, search, statusCode, fromDate, toDate, organisationId, createdBy, clientId } = filters;
    let url = `${API_URL}/api/sops?skip=${skip}&limit=${limit}`;
    
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (statusCode) url += `&status_code=${statusCode}`;
    if (fromDate) url += `&from_date=${fromDate}`;
    if (toDate) url += `&to_date=${toDate}`;
    if (organisationId) url += `&organisation_id=${organisationId}`;
    if (createdBy) url += `&created_by=${createdBy}`;
    if (clientId) url += `&client_id=${clientId}`;

    const response = await apiClient(url);
    if (!response.ok) throw new Error('Failed to fetch SOPs');
    const data = await response.json();
    return {
      sops: (data?.sops || []).map(mapExampleToSOP),
      total: data?.total || 0
    };
  },
  getSOPStats: async (): Promise<{
    totalSOPs: number;
    activeSOPs: number;
    inactiveSOPs: number;
  }> => {
    const response = await apiClient(`${API_URL}/api/sops/stats`);
    if (!response.ok) throw new Error('Failed to fetch SOP stats');
    const data = await response.json();

    return {
      totalSOPs: data.total_sops,
      activeSOPs: data.active_sops,
      inactiveSOPs: data.inactive_sops
    };
  },

processSOPDocuments: async (sopId: string): Promise<{ message: string; queued: number; document_ids?: string[] }> => {
    const response = await apiClient(`${API_URL}/api/sops/${sopId}/documents/process`, {
      method: "POST"
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to trigger document processing');
    }

    return response.json();
  },
  askSOPQuestion: async (
  sopId: string,
  question: string
): Promise<SOPQuestionResponse> => {
  const response = await apiClient(
    `${API_URL}/api/sops/${sopId}/ask`,
    {
      method: "POST",
      body: JSON.stringify({ question }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to get answer from SOP");
  }

  return response.json();
},
checkSOPExistence: async (
  clientId: string,
  providerIds: string[]
): Promise<{ exists: boolean }> => {

  const response = await apiClient(
    `${API_URL}/api/sops/check-client-sop`,
    {
      method: "POST",
      body: JSON.stringify({
        client_id: clientId,
        provider_ids: providerIds
      })
    }
  );

  if (!response.ok) throw new Error("Failed to check SOP existence");
  return response.json();
},
checkProviders: async (
  clientId: string,
  providerIds: string[],
  sopId?: string   // 👈 optional
) => {
  const res = await apiClient(
    `${API_URL}/api/sops/check-providers/${clientId}`,
    {
      method: "POST",
      body: JSON.stringify({
        provider_ids: providerIds,
        sop_id: sopId ?? null   // send null to backend if undefined
      })
    }
  );

  return res.json();
},

uploadAndExtractSOP: async (file: File, signal?: AbortSignal) => {
    const formData = new FormData();
    formData.append("file", file);

    const token = localStorage.getItem("access_token");

    const response = await fetch(
      `${API_URL}/api/sops/ai/extract-sop`,
      {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      }
    );

    if (!response.ok) {
      throw new Error(await response.text());
    }

    return response.json();
  },

  backgroundUploadAndExtractSOP: async (formData: FormData, signal?: AbortSignal) => {
  const token = localStorage.getItem("access_token");

  const response = await fetch(
    `${API_URL}/api/sops/background/ai/extract-sop`,
    {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      signal,
    }
  );

  if (!response.ok) {
    throw new Error(await response.text());
  }

  return response.json();
},
  extractSOPForeground : async (formData: FormData, signal?: AbortSignal) => {
  const token = localStorage.getItem("access_token");

  const response = await fetch(
    `${API_URL}/api/sops/ai/extract-sop-foreground`,
    {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      signal,
    }
  );

  if (!response.ok) {
    throw new Error(await response.text());
  }

  return response.json();
},
createFromExtracted: async (payload: {
  extracted_data: any;
  provider_type: string;
  client_id: string;
  provider_ids: string[];
}) => {
  const response = await apiClient(
    `${API_URL}/api/sops/background/ai/from-extracted`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to create SOP from extracted data");
  }

  return response.json();
},
  getSOPById: async (id: string): Promise<SOP> => {
    const response = await apiClient(`${API_URL}/api/sops/${id}`);
    if (!response.ok) throw new Error('Failed to fetch SOP');
    const data = await response.json();
    return mapExampleToSOP(data);
  },

  createSOP: async (data: any): Promise<SOP> => {
    const response = await apiClient(`${API_URL}/api/sops`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create SOP');
    }
    const responseData = await response.json();
    return normalizeSOP(mapExampleToSOP(responseData));
  },

  updateSOP: async (id: string, data: any): Promise<SOP> => {
    const response = await apiClient(`${API_URL}/api/sops/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update SOP');
    }
    const responseData = await response.json();
    return normalizeSOP(mapExampleToSOP(responseData));
  },

  deleteSOP: async (id: string): Promise<void> => {
    const response = await apiClient(`${API_URL}/api/sops/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete SOP');
  },

  toggleSOPStatus: async (id: string, statusId: number): Promise<SOP> => {
    const response = await apiClient(`${API_URL}/api/sops/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status_id: statusId })
    });
    if (!response.ok) throw new Error('Failed to update SOP status');
    const data = await response.json();
    return normalizeSOP(mapExampleToSOP(data));
  },

  downloadSOPPDF: async (id: string, title: string): Promise<void> => {
    const response = await apiClient(`${API_URL}/api/sops/${id}/pdf`);
    if (!response.ok) throw new Error('Failed to download PDF');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/\s+/g, '_')}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },

  reanalyseSOP: async (id: string): Promise<{ message: string; sop_id: string }> => {
    const response = await apiClient(`${API_URL}/api/sops/sops/${id}/reanalyse`, {
      method: "POST"
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || "Failed to reanalyse SOP");
    }
    return response.json();
  },

  stopSOP: async (id: string): Promise<{ message: string; sop_id: string }> => {
    const response = await apiClient(`${API_URL}/api/sops/sops/${id}/stop`, {
      method: "POST"
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || "Failed to stop SOP extraction");
    }
    return response.json();
  },
  
  downloadSourceFile: async (id: string, filename: string): Promise<void> => {
    const response = await apiClient(`${API_URL}/api/sops/${id}/source-file`);
    if (!response.ok) throw new Error('Failed to download source file');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },
  
  uploadSOPDocument: async (sopId: string, file: File, category: string = "Source file", extractedData?: any): Promise<SOPDocument> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("category", category);
    
    if (extractedData) {
      formData.append("extracted_data", JSON.stringify(extractedData));
    }

    const token = localStorage.getItem("access_token");
    const response = await fetch(`${API_URL}/api/sops/${sopId}/documents`, {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(await response.text());
    }

    return response.json();
  },

  deleteSOPDocument: async (sopId: string, documentId: string): Promise<void> => {
    const response = await apiClient(`${API_URL}/api/sops/${sopId}/documents/${documentId}`, {
      method: "DELETE"
    });
    if (!response.ok) throw new Error('Failed to delete document');
  }
};

export default sopService;
